/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../../../logger';
import type { Facts } from '../../../rules';
import { Page } from '../../page';
import { Placement } from '../../placement';
import { AstPlacement } from './placement';
import { SchEventDispatcher } from './sch/events';

import type { AdCommunicatorEvents } from '../../../messaging';
import type { AdData, CampaignData } from '../../../types';
import type { PrebidAdUnit } from '../../../types/prebid';
import type { SiteConfig } from '../../config';

const logger = Logger.getInstance();

class AstPage extends Page {
  private static astInstance: AstPage;
  private siteEventDispatcher: SchEventDispatcher;

  private constructor(siteConfig: SiteConfig) {
    super(siteConfig);
    this.siteEventDispatcher = SchEventDispatcher.getInstance();
    logger.debug('AST Page instance created');
  }

  public static getInstance(siteConfig: SiteConfig): AstPage {
    if (!AstPage.astInstance) {
      AstPage.astInstance = new AstPage(siteConfig);
      AstPage.astInstance.init();
    }
    return AstPage.astInstance;
  }

  public init(): void {
    this.siteEventDispatcher.startListening();
  }

  protected createPlacement(
    adUnit: PrebidAdUnit,
    adUnitTransformer: (adUnitCode: string) => string,
    campaignData: CampaignData[],
    siteConfig: SiteConfig,
  ): Placement | undefined {
    return new AstPlacement(adUnit, adUnitTransformer, this.removeLineIdentifiers(campaignData), siteConfig, this.siteEventDispatcher);
  }

  private removeLineIdentifiers(campaignData: CampaignData[]): CampaignData[] {
    return campaignData.map((campaign) => ({
      ...campaign,
      ads: campaign.ads.map(({ geid: _geid, elid: _elid, ...ad }: AdData) => ad),
    }));
  }

  /**
   * Extracts AST page-level targeting via apntag.requests.keywords
   * Returns normalized facts with 'page.kvs.' prefix
   */
  protected extractAdServerTargeting(): Facts {
    try {
      const keywords = window.apntag?.requests?.keywords;
      if (keywords) {
        // apntag.requests.keywords supports both primitives and arrays of primitives
        return this.normalizeAdServerTargeting(keywords);
      }
    } catch (error) {
      logger.warn(`Error extracting AST ad server targeting: ${error}`);
    }
    return {};
  }

  protected placementAdInteractionHandler(event: {
    data: AdCommunicatorEvents['AdInteraction'];
  }): void {
    logger.debug('AST Page received Ad Interaction event from AdCommunicator');
    const placement: AstPlacement = this.locateContainingPlacement(event) as AstPlacement;
    if (!placement) {
      logger.error('Ad interaction event received, but no containing placement found');
      return;
    }
    placement.dispatchAdInteraction();
  }
}

export { AstPage };
